import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Spinner from 'shared/components/spinner/spinner';
import { PermissionList } from 'shared/interface/enum';
import { checkPermission } from 'shared/util/permission';
import {
	AiConversation,
	AiConversationMessage,
	AiAskResponse,
	AI_MESSAGE_LINK_HIGHLIGHT_MS,
	getConversationTitle,
	isAiMessageError
} from '../interface/ai';
import { askAi, fetchConversation } from '../util/aiActions';
import AiMessageItem from './aiMessageItem';
import PromptComposer from './promptComposer';

interface Props {
	selectedConversation: AiConversation | null;
	isNewChat: boolean;
	targetMessageId?: string;
	onConversationCreated: (conversation: AiConversation) => void;
	onConversationUpdated: (conversation: AiConversation) => void;
}

const appendOptimisticAdminMessage = (
	messages: AiConversationMessage[],
	prompt: string,
	targetConversationId: string,
	messageId: string
): AiConversationMessage[] => [
	...messages,
	{
		messageId,
		conversationId: targetConversationId,
		role: 'admin',
		content: prompt,
		createdAt: new Date().toISOString()
	}
];

const stripOptimisticAdminMessages = (messages: AiConversationMessage[]): AiConversationMessage[] =>
	messages.filter((message) => !message.messageId.startsWith('temp-admin-'));

const stripOptimisticAdminMessage = (messages: AiConversationMessage[], messageId: string): AiConversationMessage[] =>
	messages.filter((message) => message.messageId !== messageId);

const buildMessagesFromAskResponse = (
	messages: AiConversationMessage[],
	response: AiAskResponse,
	targetConversationId: string
): AiConversationMessage[] => {
	const withoutOptimistic = stripOptimisticAdminMessages(messages);
	const persistedMessages: AiConversationMessage[] = [];

	if (response.adminMessage?.content) {
		persistedMessages.push({
			messageId: `persisted-admin-${Date.now()}`,
			conversationId: targetConversationId,
			role: 'admin',
			content: response.adminMessage.content,
			createdAt: new Date().toISOString()
		});
	}

	if (response.aiMessage) {
		persistedMessages.push(response.aiMessage);
	}

	return [...withoutOptimistic, ...persistedMessages];
};

const ChatPanel: React.FC<Props> = ({
	selectedConversation,
	isNewChat,
	targetMessageId = '',
	onConversationCreated,
	onConversationUpdated
}) => {
	const canGet = checkPermission({ anyOnePermission: [PermissionList.AiConversationGet, PermissionList.FullAccess] });
	const canAsk = checkPermission({ anyOnePermission: [PermissionList.AiConversationAsk, PermissionList.FullAccess] });
	const canViewMessages = canGet || canAsk;

	const [messages, setMessages] = useState<AiConversationMessage[]>([]);
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState('');
	const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
	const [showScrollToBottom, setShowScrollToBottom] = useState(false);

	const messagesRef = useRef<HTMLDivElement>(null);
	const loadRequestId = useRef(0);
	const askRequestId = useRef(0);
	const skipLoadForConversationId = useRef<string | null>(null);
	const askHydratedMessagesRef = useRef<Record<string, AiConversationMessage[]>>({});
	const displayedConversationIdRef = useRef<string | null>(null);
	const pendingNewChatConversationIdRef = useRef<string | null>(null);
	const conversationIdRef = useRef('');
	const isNewChatRef = useRef(isNewChat);
	const shouldStickToBottomRef = useRef(true);
	const highlightTimeoutRef = useRef<number | null>(null);
	const targetMessageHandledRef = useRef('');
	const targetMessageReloadAttemptedRef = useRef('');
	const conversationId = selectedConversation?.conversationId ?? '';

	useEffect(() => {
		conversationIdRef.current = conversationId;
		isNewChatRef.current = isNewChat;
	}, [conversationId, isNewChat]);

	const setMessagesForConversation = useCallback(
		(targetConversationId: string | null, nextMessages: AiConversationMessage[]) => {
			displayedConversationIdRef.current = targetConversationId;
			setMessages(nextMessages);
		},
		[]
	);

	const scrollToBottom = useCallback((smooth = false) => {
		const container = messagesRef.current;
		if (!container) return;

		if (smooth) {
			container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
		} else {
			container.scrollTop = container.scrollHeight;
		}
	}, []);

	const scrollToBottomAfterPaint = useCallback(() => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => scrollToBottom(false));
		});
	}, [scrollToBottom]);

	const isNearBottom = useCallback(() => {
		const container = messagesRef.current;
		if (!container) return true;
		return container.scrollHeight - container.scrollTop - container.clientHeight < 120;
	}, []);

	const updateScrollState = useCallback(() => {
		const nearBottom = isNearBottom();
		shouldStickToBottomRef.current = nearBottom;
		setShowScrollToBottom(!nearBottom && messages.length > 0);
	}, [isNearBottom, messages.length]);

	const scrollToRecentMessages = useCallback(() => {
		scrollToBottom(true);
		shouldStickToBottomRef.current = true;
		setShowScrollToBottom(false);
		requestAnimationFrame(() => {
			requestAnimationFrame(updateScrollState);
		});
	}, [scrollToBottom, updateScrollState]);

	const scrollToMessage = useCallback((messageId: string) => {
		const container = messagesRef.current;
		if (!container) return false;

		const cards = container.querySelectorAll<HTMLElement>('[data-ai-message-id]');
		let element: HTMLElement | null = null;
		for (let index = 0; index < cards.length; index += 1) {
			if (cards[index].getAttribute('data-ai-message-id') === messageId) {
				element = cards[index];
				break;
			}
		}
		if (!element) return false;

		const containerTop = container.getBoundingClientRect().top;
		const elementTop = element.getBoundingClientRect().top;
		const offset = elementTop - containerTop + container.scrollTop;
		const targetScroll = offset - container.clientHeight / 2 + element.clientHeight / 2;

		container.scrollTo({
			top: Math.max(targetScroll, 0),
			behavior: 'smooth'
		});
		return true;
	}, []);

	const highlightMessage = useCallback((messageId: string, durationMs = AI_MESSAGE_LINK_HIGHLIGHT_MS) => {
		setHighlightedMessageId(messageId);

		if (highlightTimeoutRef.current !== null) {
			window.clearTimeout(highlightTimeoutRef.current);
		}

		highlightTimeoutRef.current = window.setTimeout(() => {
			setHighlightedMessageId(null);
			highlightTimeoutRef.current = null;
		}, durationMs);
	}, []);

	const loadMessages = useCallback(
		(targetConversationId: string) => {
			if (!canGet || !targetConversationId) return;

			const requestId = ++loadRequestId.current;
			setLoading(true);
			setError('');
			setMessagesForConversation(targetConversationId, []);

			fetchConversation(targetConversationId)
				.then((res) => {
					if (requestId !== loadRequestId.current) return;
					setMessagesForConversation(targetConversationId, res.messages ?? []);
					setLoading(false);
					if (!targetMessageId) {
						scrollToBottomAfterPaint();
						requestAnimationFrame(() => scrollToBottom(false));
					}
				})
				.catch(() => {
					if (requestId !== loadRequestId.current) return;
					setMessagesForConversation(targetConversationId, []);
					setLoading(false);
					setError('Unable to load conversation messages.');
				});
		},
		[canGet, scrollToBottom, scrollToBottomAfterPaint, setMessagesForConversation, targetMessageId]
	);

	useEffect(() => {
		targetMessageHandledRef.current = '';
		targetMessageReloadAttemptedRef.current = '';
		shouldStickToBottomRef.current = !targetMessageId;
	}, [conversationId, targetMessageId]);

	useEffect(() => {
		return () => {
			if (highlightTimeoutRef.current !== null) {
				window.clearTimeout(highlightTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const container = messagesRef.current;
		if (!container) return undefined;

		container.addEventListener('scroll', updateScrollState, { passive: true });
		updateScrollState();
		return () => container.removeEventListener('scroll', updateScrollState);
	}, [updateScrollState, conversationId, loading, messages.length]);

	useEffect(() => {
		if (!targetMessageId || loading) return;
		if (displayedConversationIdRef.current !== conversationId) return;
		if (targetMessageHandledRef.current === `${conversationId}:${targetMessageId}`) return;

		const targetKey = `${conversationId}:${targetMessageId}`;
		const messageExists = messages.some((message) => message.messageId === targetMessageId);

		if (!messageExists) {
			const reloadWasAttempted = targetMessageReloadAttemptedRef.current === targetKey;
			if (canGet && !reloadWasAttempted) return;

			targetMessageHandledRef.current = targetKey;
			toast.info('Message not found in this conversation.');
			return;
		}

		shouldStickToBottomRef.current = false;
		let cancelled = false;

		const tryScrollToTarget = (attempt = 0) => {
			if (cancelled || targetMessageHandledRef.current === targetKey) return;
			if (displayedConversationIdRef.current !== conversationId) return;

			if (scrollToMessage(targetMessageId)) {
				highlightMessage(targetMessageId);
				targetMessageHandledRef.current = targetKey;
				updateScrollState();
				return;
			}

			if (attempt < 15) {
				requestAnimationFrame(() => tryScrollToTarget(attempt + 1));
			}
		};

		requestAnimationFrame(() => {
			requestAnimationFrame(() => tryScrollToTarget());
		});

		return () => {
			cancelled = true;
		};
	}, [
		canGet,
		conversationId,
		highlightMessage,
		loading,
		messages,
		scrollToMessage,
		targetMessageId,
		updateScrollState
	]);

	useEffect(() => {
		if (isNewChat || !conversationId) {
			loadRequestId.current += 1;
			setLoading(false);
			setError('');
			if (displayedConversationIdRef.current !== null) {
				setMessagesForConversation(null, []);
			}
			return;
		}

		const isShowingCurrentConversation = displayedConversationIdRef.current === conversationId;
		const cachedMessages = askHydratedMessagesRef.current[conversationId];
		const isPendingServerHydration = skipLoadForConversationId.current === conversationId;

		if (!canGet) {
			loadRequestId.current += 1;
			if (!isShowingCurrentConversation) {
				setMessagesForConversation(conversationId, cachedMessages ?? []);
			}
			setLoading(false);
			setError('');
			return;
		}

		if (isPendingServerHydration) {
			if (!isShowingCurrentConversation) {
				setMessagesForConversation(conversationId, cachedMessages ?? []);
				setLoading(false);
				setError('');
			}
			return;
		}

		const targetReloadKey = targetMessageId ? `${conversationId}:${targetMessageId}` : '';
		const needsReloadForTargetMessage =
			!!targetMessageId &&
			isShowingCurrentConversation &&
			targetMessageReloadAttemptedRef.current !== targetReloadKey &&
			!messages.some((message) => message.messageId === targetMessageId);

		if (!isShowingCurrentConversation) {
			if (targetMessageId) {
				targetMessageReloadAttemptedRef.current = targetReloadKey;
			}
			loadMessages(conversationId);
		} else if (needsReloadForTargetMessage) {
			targetMessageReloadAttemptedRef.current = targetReloadKey;
			loadMessages(conversationId);
		}
	}, [canGet, conversationId, isNewChat, loadMessages, messages, setMessagesForConversation, targetMessageId]);

	useEffect(() => {
		if (!loading && messages.length > 0 && shouldStickToBottomRef.current && !targetMessageId) {
			scrollToBottomAfterPaint();
		}
	}, [loading, messages.length, scrollToBottomAfterPaint, targetMessageId]);

	useEffect(() => {
		if (sending && shouldStickToBottomRef.current) {
			scrollToBottomAfterPaint();
		}
	}, [sending, messages.length, scrollToBottomAfterPaint]);

	const handleSend = useCallback(
		(prompt: string): Promise<void> => {
			if (!canAsk) {
				toast.error('You do not have permission to ask AI.');
				return Promise.reject();
			}

			setSending(true);
			const optimisticMessageId = `temp-admin-${Date.now()}`;
			setMessages((prev) =>
				appendOptimisticAdminMessage(prev, prompt, conversationId || 'pending', optimisticMessageId)
			);
			if (shouldStickToBottomRef.current) {
				scrollToBottomAfterPaint();
			}
			const requestId = ++askRequestId.current;
			const sentForConversationId = conversationId;
			const payload = {
				prompt,
				...(conversationId ? { conversationId } : {})
			};

			const isStaleAskResponse = (): boolean => {
				if (requestId !== askRequestId.current) return true;
				if (sentForConversationId) {
					return conversationIdRef.current !== sentForConversationId;
				}
				const pendingConversationId = pendingNewChatConversationIdRef.current;
				if (pendingConversationId) {
					return conversationIdRef.current !== pendingConversationId;
				}
				return !isNewChatRef.current || conversationIdRef.current !== '';
			};

			const cacheAskMessages = (targetConversationId: string, nextMessages: AiConversationMessage[]) => {
				askHydratedMessagesRef.current[targetConversationId] = nextMessages;
			};

			const clearPendingServerHydration = (targetConversationId: string) => {
				if (skipLoadForConversationId.current === targetConversationId) {
					skipLoadForConversationId.current = null;
				}
			};

			return askAi(payload)
				.then((res) => {
					if (isStaleAskResponse()) return;

					const nextConversation = res.conversation;
					const targetConversationId = nextConversation.conversationId;

					const applyAskMessages = () => {
						setMessages((prev) => {
							const nextMessages = buildMessagesFromAskResponse(
								stripOptimisticAdminMessage(prev, optimisticMessageId),
								res,
								targetConversationId
							);
							cacheAskMessages(targetConversationId, nextMessages);
							displayedConversationIdRef.current = targetConversationId;
							return nextMessages;
						});
						if (shouldStickToBottomRef.current) {
							scrollToBottomAfterPaint();
						}
					};

					if (!sentForConversationId) {
						if (canGet) {
							skipLoadForConversationId.current = targetConversationId;
						}
						pendingNewChatConversationIdRef.current = targetConversationId;
						conversationIdRef.current = targetConversationId;
						isNewChatRef.current = false;
						onConversationCreated(nextConversation);
						applyAskMessages();

						if (isAiMessageError(res.aiMessage)) {
							toast.error(res.aiMessage.metadata?.message || 'Failed to process AI request.');
						}

						if (!canGet) {
							return;
						}

						return fetchConversation(targetConversationId)
							.then((detail) => {
								if (isStaleAskResponse()) return;
								if (detail.messages?.length) {
									setMessagesForConversation(targetConversationId, detail.messages);
									delete askHydratedMessagesRef.current[targetConversationId];
									if (shouldStickToBottomRef.current && !targetMessageId) {
										scrollToBottomAfterPaint();
									}
								}
							})
							.catch(() => {
								if (isStaleAskResponse()) return;
								toast.error('Unable to refresh conversation messages.');
							})
							.finally(() => {
								clearPendingServerHydration(targetConversationId);
							});
					}

					onConversationUpdated(nextConversation);
					applyAskMessages();

					if (isAiMessageError(res.aiMessage)) {
						toast.error(res.aiMessage.metadata?.message || 'Failed to process AI request.');
					}

					if (!canGet) {
						return;
					}

					return fetchConversation(targetConversationId)
						.then((detail) => {
							if (isStaleAskResponse()) return;
							if (detail.messages?.length) {
								setMessagesForConversation(targetConversationId, detail.messages);
								delete askHydratedMessagesRef.current[targetConversationId];
								if (shouldStickToBottomRef.current && !targetMessageId) {
									scrollToBottomAfterPaint();
								}
							}
						})
						.catch(() => {
							if (isStaleAskResponse()) return;
							toast.error('Unable to refresh conversation messages.');
						});
				})
				.catch(() => {
					if (requestId !== askRequestId.current) return;
					setMessages((prev) => stripOptimisticAdminMessage(prev, optimisticMessageId));
					toast.error('Failed to get a response from AI.');
					throw new Error('ask failed');
				})
				.finally(() => {
					if (requestId === askRequestId.current) {
						pendingNewChatConversationIdRef.current = null;
					}
					setSending(false);
				});
		},
		[
			canAsk,
			canGet,
			conversationId,
			onConversationCreated,
			onConversationUpdated,
			scrollToBottomAfterPaint,
			setMessagesForConversation,
			targetMessageId
		]
	);

	if (!selectedConversation && !isNewChat) {
		return (
			<div className="telegram-conversations__messages telegram-conversations__messages--empty">
				<div className="telegram-conversations__empty-state">
					<i className="fa fa-magic" />
					<h4>Start a conversation</h4>
					<p>Select a chat from the left or create a new one to ask Claude.</p>
				</div>
			</div>
		);
	}

	const title = isNewChat ? 'New chat' : getConversationTitle(selectedConversation as AiConversation);

	return (
		<div className="telegram-conversations__messages">
			<div className="telegram-conversations__panel-header">
				<div className="telegram-conversations__panel-title">
					<span className="telegram-conversations__chat-avatar">{title.slice(0, 1).toUpperCase()}</span>
					<div>
						<h5>{title}</h5>
						<span className="telegram-conversations__chat-subtitle">
							{selectedConversation?.aiModel ? `Model: ${selectedConversation.aiModel}` : 'Claude AI'}
						</span>
					</div>
				</div>
			</div>

			<div className="telegram-conversations__message-area">
				{!canViewMessages && !isNewChat ? (
					<div className="telegram-conversations__empty">
						<i className="fa fa-lock" />
						<span>You do not have permission to view messages.</span>
					</div>
				) : (
					<>
						<div
							className="telegram-conversations__message-list ai-assistant__message-list"
							ref={messagesRef}
						>
							{loading ? <Spinner /> : null}
							{error ? (
								<div className="telegram-conversations__empty telegram-conversations__empty--error">
									{error}
								</div>
							) : null}
							{!loading && !error && messages.length === 0 && !sending ? (
								<div className="telegram-conversations__empty">
									<i className="fa fa-comment-o" />
									<span>{isNewChat ? 'Send your first message below.' : 'No messages found.'}</span>
								</div>
							) : null}
							{messages.map((message, index) => {
								const nextMessage = messages[index + 1];
								return (
									<AiMessageItem
										key={message.messageId}
										message={message}
										hasErrorResponse={
											message.role === 'admin' && !!nextMessage && isAiMessageError(nextMessage)
										}
										isHighlighted={highlightedMessageId === message.messageId}
									/>
								);
							})}
							{sending ? (
								<div className="ai-assistant__thinking">
									<Spinner />
									<span>Claude is thinking...</span>
								</div>
							) : null}
						</div>
						{showScrollToBottom ? (
							<button
								type="button"
								className="telegram-conversations__recent-btn"
								onClick={scrollToRecentMessages}
								title="Go to latest messages"
								aria-label="Go to latest messages"
							>
								<i className="fa fa-chevron-down" />
							</button>
						) : null}
					</>
				)}
			</div>

			{canAsk ? (
				<PromptComposer
					conversationKey={isNewChat ? 'new' : conversationId}
					onSend={handleSend}
					sending={sending}
					disabled={!canAsk}
				/>
			) : (
				<div className="telegram-conversations__read-only-notice">You do not have permission to ask AI.</div>
			)}
		</div>
	);
};

export default ChatPanel;
